import { useEffect, useRef } from 'react';

import { useT } from '../../i18n';
import { appendAppLog } from '../../platform/appLog';
import { useAppStore } from '../../store/useAppStore';
import { Icon } from '../../ui/Icon';
import styles from './SimulativBand.module.css';

/**
 * Der stehende Hinweis, dass diese Prüfung nichts aufzeichnet.
 *
 * **Vorgabe des Nutzers vom 2026-09-18**, wörtlich: *„es soll sowohl bei der Prüfungsauswahl
 * prominent markiert werden als auch während der Prüfung dies ständig als Warnung gezeigt
 * werden."* Also kein Hinweis, der einmal aufblitzt, sondern ein Streifen, der stehen bleibt.
 *
 * ⚠️ **Er kostet Platz, und das ist hingenommen.** Auf die Rückfrage, ob der Bogen dafür
 * schmaler werden darf, kam: *„Ist aber okay. Ist wichtig, dass man das sieht."* Ein Prüfer,
 * der eine Station bewertet und nicht weiss, dass die Eingaben verworfen werden, bewertet ins
 * Leere — und merkt es erst, wenn die Ergebnisse fehlen.
 *
 * ⚠️ **Nicht in die Fusszeile.** Die trägt Absenden, Stationskommentar und Schlafknopf und ist
 * beim Abgeben der belebteste Ort. Der Streifen steht unter der Kopfzeile, wo bis zum
 * 2026-09-17 das Offline-Band sass.
 */
export function SimulativBand() {
  const t = useT();
  const simulativ = useAppStore((s) => s.exam?.simulativ === true);
  const examId = useAppStore((s) => s.examId);

  /**
   * Einmal je Prüfung ins Dateilog.
   *
   * ⚠️ **Ohne diese Zeile liesse sich hinterher nicht belegen, warum nichts angekommen ist.**
   * Die Protokollzeilen werden örtlich abgehakt, ohne gesendet zu werden (`server/sync.ts`) —
   * von aussen sieht das aus wie eine gelungene Übertragung. Das Dateilog ist die einzige
   * Stelle, an der später steht, dass hier absichtlich nichts hinausging.
   *
   * Der Merker verhindert eine Zeile bei jedem Rerender; beim Wechsel der Prüfung entsteht
   * wieder eine.
   */
  const gemeldet = useRef<string | null>(null);
  useEffect(() => {
    if (!simulativ || !examId || gemeldet.current === examId) return;
    gemeldet.current = examId;
    void appendAppLog(
      'Simulationsprüfung ' + examId + ': nichts wird aufgezeichnet — ' +
        'Bewertungen, Kommentare und Aufnahmen bleiben auf diesem Gerät.',
    );
  }, [simulativ, examId]);

  if (!simulativ) return null;

  return (
    <div className={styles.band} role="status">
      <Icon name="warning" size={15} />
      <span>
        <strong>{t.setup.simulativ}</strong> — {t.setup.simulativNote}
      </span>
    </div>
  );
}
